<template>
  <v-container fluid class="ncrm-deals-home">
    <NcrmHomePageHeader
      icon="mdi-handshake-outline"
      :title="$t('ncrm.deals.home.title')"
      :subtitle="$t('ncrm.deals.home.subtitle')"
      data-tour="deals-header"
    >
      <v-btn variant="text" color="primary" :loading="isLoading" @click="loadHome">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("ncrm.deals.actions.refresh") }}
      </v-btn>
      <v-btn variant="text" color="primary" @click="widgetManagerDialog = true">
        <v-icon start>mdi-view-dashboard-edit-outline</v-icon>
        {{ $t("ncrm.deals.home.widgets.configure") }}
      </v-btn>
      <v-btn color="primary" data-tour="deals-create-board" @click="createBoardDialog = true">
        <v-icon start>mdi-plus</v-icon>
        {{ $t("ncrm.deals.actions.createBoard") }}
      </v-btn>
    </NcrmHomePageHeader>
    <NcrmHomeKpiCards class="mt-2" :items="globalKpis" data-tour="deals-kpi" />

    <v-row class="mt-2" dense data-tour="deals-widgets">
      <v-col cols="12" v-if="!activeWidgetItems.length">
        <v-alert type="info">
          {{ $t("ncrm.deals.home.widgets.noneSelected") }}
        </v-alert>
      </v-col>

      <v-col
        v-for="widget in activeWidgetItems"
        :key="widget.key"
        :cols="widget.layout.cols"
        :md="widget.layout.md"
        :lg="widget.layout.lg"
      >
        <v-card
          v-if="widget.key === 'trend'"
          class="ncrm-deals-home__widget-card h-100"
        >
          <v-card-title class="d-flex flex-wrap align-center justify-space-between ga-2">
            <span>{{ $t("ncrm.deals.home.trend.title") }}</span>
            <v-chip color="primary">
              {{ $t("ncrm.deals.home.trend.subtitle") }}
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-skeleton-loader v-if="widgetsLoading" type="article" />
            <div v-else-if="!trendSeries.length" class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.deals.home.trend.empty") }}
            </div>
            <div v-else>
              <div class="text-caption text-medium-emphasis mb-3">
                {{
                  $t("ncrm.deals.home.trend.delta", {
                    delta: formatSignedNumber(trendDelta.delta_total),
                    percent: trendDelta.delta_percent_label,
                  })
                }}
              </div>
              <div
                v-for="item in trendSeries"
                :key="item.date"
                class="d-flex align-center ga-3 mb-2 ncrm-deals-home__trend-row"
              >
                <div class="text-caption text-medium-emphasis ncrm-deals-home__trend-date">
                  {{ item.label }}
                </div>
                <div class="flex-grow-1">
                  <v-progress-linear
                    :model-value="item.total_percent"
                    color="primary"
                    height="8"
                    rounded
                  />
                </div>
                <div class="text-caption ncrm-text-strong ncrm-deals-home__trend-total">
                  {{ formatNumber(item.total) }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-else-if="widget.key === 'statusDistribution'"
          class="ncrm-deals-home__widget-card h-100"
        >
          <v-card-title>{{ $t("ncrm.deals.home.statusDistribution.title") }}</v-card-title>
          <v-divider />
          <v-card-text>
            <v-skeleton-loader v-if="widgetsLoading" type="chip@6" />
            <div v-else-if="!statusDistribution.length" class="text-body-2 text-medium-emphasis">
              {{ $t("ncrm.deals.home.statusDistribution.empty") }}
            </div>
            <div v-else class="d-flex flex-wrap ga-2">
              <v-chip
                v-for="item in statusDistribution"
                :key="item.status"
                :color="statusColor(item.status)"
              >
                {{ statusLabel(item.status) }} · {{ formatNumber(item.count) }}
              </v-chip>
            </div>
          </v-card-text>
        </v-card>

        <v-card
          v-else-if="widget.key === 'boardMetrics'"
          class="ncrm-deals-home__widget-card h-100"
        >
          <v-card-title>{{ $t("ncrm.deals.home.boardWidget.title") }}</v-card-title>
          <v-divider />
          <v-card-text>
            <NcrmHomeBoardMetrics
              :loading="isLoading"
              :items="boardWidgetItems"
              :empty-message="$t('ncrm.deals.home.boardWidget.empty')"
              :eyebrow-label="$t('ncrm.deals.home.headers.name')"
              :open-label="$t('ncrm.deals.actions.openBoard')"
              @open="openBoard"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-card class="mt-3" data-tour="deals-boards">
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="boards"
          item-key="id"
          class="elevation-0 ncrm-deals-home__table"
          density="comfortable"
          :loading="boardsLoading"
          :row-props="() => ({ style: 'cursor:pointer' })"
          @click:row="handleBoardRowClick"
        >
          <template #item.name="{ item }">
            <div class="d-flex align-center ga-2">
              <v-icon size="14" style="opacity:0.5">mdi-view-column-outline</v-icon>
              <span class="text-body-2 ncrm-text-strong">{{ item.name }}</span>
            </div>
          </template>

          <template #item.open_deal_count="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "open_deals", item.open_deal_count)) }}
          </template>

          <template #item.deal_count="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "total_deals", item.deal_count)) }}
          </template>

          <template #item.overdue_followups="{ item }">
            <span
              class="ncrm-deals-home__table-overdue"
              :class="{ 'ncrm-deals-home__table-overdue--active': boardMetricValue(item.id, 'overdue_followups') > 0 }"
            >
              {{ formatNumber(boardMetricValue(item.id, "overdue_followups")) }}
            </span>
          </template>

          <template #item.touched_today="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "touched_today")) }}
          </template>

          <template #item.won_week="{ item }">
            <span style="color: rgb(var(--v-theme-success))">
              {{ formatNumber(boardMetricValue(item.id, "won_week")) }}
            </span>
          </template>

          <template #item.lost_week="{ item }">
            {{ formatNumber(boardMetricValue(item.id, "lost_week")) }}
          </template>

          <template #item.member_count="{ item }">
            {{ formatNumber(item.member_count) }}
          </template>

          <template #item.updated_at="{ item }">
            <div class="d-flex align-center ga-1 ncrm-deals-home__table-date">
              <v-icon size="13" style="opacity:0.38">mdi-clock-outline</v-icon>
              {{ formatDateTime(item.updated_at) }}
            </div>
          </template>

          <template #item.row_nav>
            <v-icon size="18" class="ncrm-deals-home__table-chevron">mdi-chevron-right</v-icon>
          </template>

          <template #no-data>
            <div class="text-body-2 text-medium-emphasis pa-6 text-center">
              {{ $t("ncrm.deals.home.empty") }}
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="createBoardDialog" max-width="560">
      <v-card>
        <v-card-title>{{ $t("ncrm.deals.home.createBoard.title") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="createBoardForm.name"
            :label="$t('ncrm.deals.home.createBoard.name')"
            autofocus
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="closeCreateBoardDialog">
            {{ $t("app.actions.cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="createBoardLoading"
            :disabled="createBoardLoading || !createBoardForm.name.trim()"
            @click="submitCreateBoard"
          >
            {{ $t("app.actions.create") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="widgetManagerDialog" max-width="760">
      <v-card class="ncrm-deals-home__widget-manager">
        <v-card-title>{{ $t("ncrm.deals.home.widgets.manageTitle") }}</v-card-title>
        <v-divider />
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 ncrm-text-strong mb-2">
                {{ $t("ncrm.deals.home.widgets.activeTitle") }}
              </div>
              <v-alert v-if="!activeWidgetItems.length" type="info" density="compact">
                {{ $t("ncrm.deals.home.widgets.emptyActive") }}
              </v-alert>
              <v-list v-else density="compact" class="ncrm-deals-home__widget-list">
                <v-list-item
                  v-for="(item, index) in activeWidgetItems"
                  :key="`active-${item.key}`"
                  :title="item.label"
                >
                  <template #append>
                    <div class="d-flex align-center ga-1">
                      <v-btn
                        icon="mdi-arrow-up"
                        size="x-small"
                        variant="text"
                        :disabled="index === 0"
                        :aria-label="$t('ncrm.deals.home.widgets.moveUp')"
                        @click="moveWidget(item.key, -1)"
                      />
                      <v-btn
                        icon="mdi-arrow-down"
                        size="x-small"
                        variant="text"
                        :disabled="index === activeWidgetItems.length - 1"
                        :aria-label="$t('ncrm.deals.home.widgets.moveDown')"
                        @click="moveWidget(item.key, 1)"
                      />
                      <v-btn
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        color="error"
                        :aria-label="$t('ncrm.deals.home.widgets.remove')"
                        @click="removeWidget(item.key)"
                      />
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-subtitle-2 ncrm-text-strong mb-2">
                {{ $t("ncrm.deals.home.widgets.availableTitle") }}
              </div>
              <v-alert v-if="!availableWidgetItems.length" type="success" density="compact">
                {{ $t("ncrm.deals.home.widgets.emptyAvailable") }}
              </v-alert>
              <v-list v-else density="compact" class="ncrm-deals-home__widget-list">
                <v-list-item
                  v-for="item in availableWidgetItems"
                  :key="`available-${item.key}`"
                  :title="item.label"
                >
                  <template #append>
                    <v-btn
                      icon="mdi-plus"
                      size="x-small"
                      variant="text"
                      color="primary"
                      :aria-label="$t('ncrm.deals.home.widgets.add')"
                      @click="addWidget(item.key)"
                    />
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="widgetManagerDialog = false">
            {{ $t("app.actions.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3200">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import NcrmHomeBoardMetrics from "@/components/ncrm/shared/NcrmHomeBoardMetrics.vue";
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import { formatDisplayNumber, formatDisplayDayMonth } from "@/Utile/displayFormat.mjs";

const DEALS_HOME_WIDGET_STORAGE_KEY = "ncrm.deals.home.widgets.v1";
const DEALS_HOME_WIDGETS_CATALOG = Object.freeze([
  Object.freeze({
    key: "trend",
    labelKey: "ncrm.deals.home.widgets.trend",
    layout: Object.freeze({ cols: 12, md: 12, lg: 8 }),
  }),
  Object.freeze({
    key: "statusDistribution",
    labelKey: "ncrm.deals.home.widgets.statusDistribution",
    layout: Object.freeze({ cols: 12, md: 12, lg: 4 }),
  }),
  Object.freeze({
    key: "boardMetrics",
    labelKey: "ncrm.deals.home.widgets.boardMetrics",
    layout: Object.freeze({ cols: 12, md: 12, lg: 12 }),
  }),
]);
const DEALS_HOME_WIDGET_KEYS = Object.freeze(DEALS_HOME_WIDGETS_CATALOG.map((item) => item.key));
const DEALS_HOME_DEFAULT_ACTIVE_WIDGET_KEYS = Object.freeze([...DEALS_HOME_WIDGET_KEYS]);

const cloneDefaultActiveWidgetKeys = () => [...DEALS_HOME_DEFAULT_ACTIVE_WIDGET_KEYS];

const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export default {
  name: "NcrmDealsHome",
  components: {
    NcrmHomeBoardMetrics,
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
  },
  data() {
    return {
      boardsLoading: false,
      widgetsLoading: false,
      createBoardLoading: false,
      createBoardDialog: false,
      widgetManagerDialog: false,
      createBoardForm: {
        name: "",
      },
      boards: [],
      homeSummary: {
        totals: {
          boards: 0,
          total_deals: 0,
          open_deals: 0,
          won_this_month: 0,
          lost_this_month: 0,
          overdue_followups: 0,
        },
        status_distribution: [],
        board_metrics: [],
      },
      homeTrend: {
        totals: {
          delta_total: 0,
          delta_percent: null,
        },
        series: [],
      },
      activeWidgetKeys: cloneDefaultActiveWidgetKeys(),
      snackbar: {
        show: false,
        color: "info",
        message: "",
      },
    };
  },
  computed: {
    isLoading() {
      return this.boardsLoading || this.widgetsLoading;
    },
    widgetCatalog() {
      return DEALS_HOME_WIDGETS_CATALOG.map((item) => ({
        key: item.key,
        label: this.$t(item.labelKey),
        layout: item.layout,
      }));
    },
    activeWidgetItems() {
      return this.activeWidgetKeys
        .filter((key) => DEALS_HOME_WIDGET_KEYS.includes(key))
        .map((key) => this.widgetCatalog.find((item) => item.key === key))
        .filter(Boolean);
    },
    availableWidgetItems() {
      const activeSet = new Set(this.activeWidgetItems.map((item) => item.key));
      return this.widgetCatalog.filter((item) => !activeSet.has(item.key));
    },
    headers() {
      return [
        { title: this.$t("ncrm.deals.home.headers.name"), key: "name" },
        { title: this.$t("ncrm.deals.home.headers.openDeals"), key: "open_deal_count", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.totalDeals"), key: "deal_count", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.overdue"), key: "overdue_followups", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.touchedToday"), key: "touched_today", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.wonWeek"), key: "won_week", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.lostWeek"), key: "lost_week", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.members"), key: "member_count", align: "end" },
        { title: this.$t("ncrm.deals.home.headers.updatedAt"), key: "updated_at", sortable: true },
        { title: "", key: "row_nav", sortable: false, width: "48px", align: "center" },
      ];
    },
    globalKpis() {
      const totals = this.homeSummary?.totals || {};
      return [
        {
          key: "open_deals",
          label: this.$t("ncrm.deals.home.kpi.openDeals"),
          value: parseNumeric(totals.open_deals, 0),
          icon: "mdi-filter-outline",
          color: "primary",
        },
        {
          key: "overdue_followups",
          label: this.$t("ncrm.deals.home.kpi.overdueFollowups"),
          value: parseNumeric(totals.overdue_followups, 0),
          icon: "mdi-alert-circle-outline",
          color: "error",
        },
        {
          key: "won_this_month",
          label: this.$t("ncrm.deals.home.kpi.wonThisMonth"),
          value: parseNumeric(totals.won_this_month, 0),
          icon: "mdi-trophy-outline",
          color: "success",
        },
        {
          key: "lost_this_month",
          label: this.$t("ncrm.deals.home.kpi.lostThisMonth"),
          value: parseNumeric(totals.lost_this_month, 0),
          icon: "mdi-close-circle-outline",
          color: "warning",
        },
      ];
    },
    statusDistribution() {
      const rows = Array.isArray(this.homeSummary?.status_distribution) ? this.homeSummary.status_distribution : [];
      return rows
        .map((row) => ({
          status: String(row?.status || "").trim().toLowerCase(),
          count: parseNumeric(row?.count, 0),
        }))
        .filter((row) => row.status)
        .sort((left, right) => right.count - left.count);
    },
    boardMetricMap() {
      const map = new Map();
      const items = Array.isArray(this.homeSummary?.board_metrics) ? this.homeSummary.board_metrics : [];
      for (const item of items) {
        const boardId = Number(item?.board_id || 0);
        if (!boardId) continue;
        map.set(boardId, {
          total_deals: parseNumeric(item?.total_deals, 0),
          open_deals: parseNumeric(item?.open_deals, 0),
          overdue_followups: parseNumeric(item?.overdue_followups, 0),
          touched_today: parseNumeric(item?.touched_today, 0),
          won_week: parseNumeric(item?.won_week, 0),
          lost_week: parseNumeric(item?.lost_week, 0),
          member_count: parseNumeric(item?.member_count, 0),
          board_name: String(item?.board_name || "").trim(),
        });
      }
      return map;
    },
    boardWidgetItems() {
      const items = [];
      const seen = new Set();

      for (const board of this.boards) {
        const boardId = Number(board?.id || 0);
        if (!boardId) continue;
        const metrics = this.boardMetricMap.get(boardId) || {};
        items.push({
          id: boardId,
          name: String(board?.name || "").trim() || metrics.board_name || `#${boardId}`,
          badgeLabel: this.$t("ncrm.deals.home.headers.members"),
          badgeValue: parseNumeric(metrics.member_count, board?.member_count),
          primaryLabel: this.$t("ncrm.deals.home.boardWidget.metrics.openDeals"),
          primaryValue: parseNumeric(metrics.open_deals, board?.open_deal_count),
          primaryIcon: "mdi-filter-outline",
          primaryColor: "primary",
          metrics: [
            {
              key: "total_deals",
              label: this.$t("ncrm.deals.home.headers.totalDeals"),
              value: parseNumeric(metrics.total_deals, board?.deal_count),
              icon: "mdi-database-outline",
              color: "quaternary",
            },
            {
              key: "overdue_followups",
              label: this.$t("ncrm.deals.home.boardWidget.metrics.overdueFollowups"),
              value: parseNumeric(metrics.overdue_followups, 0),
              icon: "mdi-alert-circle-outline",
              color: "error",
            },
            {
              key: "touched_today",
              label: this.$t("ncrm.deals.home.boardWidget.metrics.touchedToday"),
              value: parseNumeric(metrics.touched_today, 0),
              icon: "mdi-clock-outline",
              color: "info",
            },
            {
              key: "won_week",
              label: this.$t("ncrm.deals.home.boardWidget.metrics.wonWeek"),
              value: parseNumeric(metrics.won_week, 0),
              icon: "mdi-trophy-outline",
              color: "success",
            },
          ],
        });
        seen.add(boardId);
      }

      for (const [boardId, metrics] of this.boardMetricMap.entries()) {
        if (seen.has(boardId)) continue;
        items.push({
          id: boardId,
          name: metrics.board_name || `#${boardId}`,
          badgeLabel: this.$t("ncrm.deals.home.headers.members"),
          badgeValue: parseNumeric(metrics.member_count, 0),
          primaryLabel: this.$t("ncrm.deals.home.boardWidget.metrics.openDeals"),
          primaryValue: parseNumeric(metrics.open_deals, 0),
          primaryIcon: "mdi-filter-outline",
          primaryColor: "primary",
          metrics: [
            {
              key: "total_deals",
              label: this.$t("ncrm.deals.home.headers.totalDeals"),
              value: parseNumeric(metrics.total_deals, 0),
              icon: "mdi-database-outline",
              color: "quaternary",
            },
            {
              key: "overdue_followups",
              label: this.$t("ncrm.deals.home.boardWidget.metrics.overdueFollowups"),
              value: parseNumeric(metrics.overdue_followups, 0),
              icon: "mdi-alert-circle-outline",
              color: "error",
            },
            {
              key: "touched_today",
              label: this.$t("ncrm.deals.home.boardWidget.metrics.touchedToday"),
              value: parseNumeric(metrics.touched_today, 0),
              icon: "mdi-clock-outline",
              color: "info",
            },
            {
              key: "won_week",
              label: this.$t("ncrm.deals.home.boardWidget.metrics.wonWeek"),
              value: parseNumeric(metrics.won_week, 0),
              icon: "mdi-trophy-outline",
              color: "success",
            },
          ],
        });
      }

      return items;
    },
    trendSeries() {
      const rows = Array.isArray(this.homeTrend?.series) ? this.homeTrend.series : [];
      const maxTotal = Math.max(1, ...rows.map((row) => parseNumeric(row?.total, 0)));
      return rows.map((row) => {
        const total = parseNumeric(row?.total, 0);
        const percent = total > 0 ? Math.max((total / maxTotal) * 100, 8) : 0;
        return {
          date: String(row?.date || "").trim(),
          label: this.formatTrendDate(row?.date),
          total,
          total_percent: percent,
        };
      });
    },
    trendDelta() {
      const totals = this.homeTrend?.totals || {};
      const deltaTotal = parseNumeric(totals?.delta_total, 0);
      const deltaPercentRaw = totals?.delta_percent;
      const deltaPercent = deltaPercentRaw === null || deltaPercentRaw === undefined
        ? null
        : parseNumeric(deltaPercentRaw, 0);
      return {
        delta_total: deltaTotal,
        delta_percent: deltaPercent,
        delta_percent_label: deltaPercent === null ? "-" : `${this.formatNumber(deltaPercent)}%`,
      };
    },
  },
  mounted() {
    this.restoreWidgetConfig();
    this.loadHome();
  },
  methods: {
    showSnackbar(message, color = "info") {
      this.snackbar = {
        show: true,
        color,
        message,
      };
    },
    formatNumber(value) {
      return formatDisplayNumber(parseNumeric(value, 0));
    },
    formatSignedNumber(value) {
      const number = parseNumeric(value, 0);
      if (number > 0) return `+${this.formatNumber(number)}`;
      return this.formatNumber(number);
    },
    formatDateTime(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(normalized.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime())) return normalized;
      const dd = String(parsed.getDate()).padStart(2, "0");
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const yyyy = parsed.getFullYear();
      const HH = String(parsed.getHours()).padStart(2, "0");
      const min = String(parsed.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${yyyy}, ${HH}:${min}`;
    },
    formatTrendDate(value) {
      const normalized = String(value || "").trim();
      if (!normalized) return "-";
      const parsed = new Date(`${normalized}T00:00:00`);
      if (Number.isNaN(parsed.getTime())) return normalized;
      return formatDisplayDayMonth(parsed);
    },
    statusLabel(value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (!normalized) return "-";
      const key = `ncrm.deals.statuses.${normalized}`;
      const label = this.$t(key);
      return label === key ? normalized : label;
    },
    statusColor(value) {
      const normalized = String(value || "").trim().toLowerCase();
      const map = { open: "primary", won: "success", lost: "error", cancelled: "warning" };
      return map[normalized] || "quaternary";
    },
    boardMetricValue(boardId, metricKey, fallback = 0) {
      const normalizedBoardId = Number(boardId || 0);
      if (!normalizedBoardId) return parseNumeric(fallback, 0);
      const metrics = this.boardMetricMap.get(normalizedBoardId);
      if (!metrics) return parseNumeric(fallback, 0);
      return parseNumeric(metrics?.[metricKey], parseNumeric(fallback, 0));
    },
    resolveTimezoneOffsetMinutes() {
      return -new Date().getTimezoneOffset();
    },
    addWidget(key) {
      const normalized = String(key || "").trim();
      if (!DEALS_HOME_WIDGET_KEYS.includes(normalized)) return;
      if (this.activeWidgetKeys.includes(normalized)) return;
      this.activeWidgetKeys = [...this.activeWidgetKeys, normalized];
      this.persistWidgetConfig();
    },
    removeWidget(key) {
      const normalized = String(key || "").trim();
      if (!normalized) return;
      this.activeWidgetKeys = this.activeWidgetKeys.filter((item) => item !== normalized);
      this.persistWidgetConfig();
    },
    moveWidget(key, direction) {
      const normalized = String(key || "").trim();
      const delta = Number(direction || 0);
      const currentIndex = this.activeWidgetKeys.findIndex((item) => item === normalized);
      if (currentIndex === -1) return;
      const targetIndex = currentIndex + (delta < 0 ? -1 : 1);
      if (targetIndex < 0 || targetIndex >= this.activeWidgetKeys.length) return;
      const next = [...this.activeWidgetKeys];
      const [item] = next.splice(currentIndex, 1);
      next.splice(targetIndex, 0, item);
      this.activeWidgetKeys = next;
      this.persistWidgetConfig();
    },
    persistWidgetConfig() {
      if (typeof window === "undefined") return;
      try {
        window.localStorage.setItem(
          DEALS_HOME_WIDGET_STORAGE_KEY,
          JSON.stringify({ active_keys: this.activeWidgetKeys })
        );
      } catch (_) {
        // ignore storage failures
      }
    },
    restoreWidgetConfig() {
      if (typeof window === "undefined") return;
      try {
        const raw = window.localStorage.getItem(DEALS_HOME_WIDGET_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return;

        const activeKeysFromV2 = Array.isArray(parsed?.active_keys)
          ? parsed.active_keys
              .map((item) => String(item || "").trim())
              .filter((item, index, source) => item && source.indexOf(item) === index)
              .filter((item) => DEALS_HOME_WIDGET_KEYS.includes(item))
          : null;

        if (activeKeysFromV2) {
          this.activeWidgetKeys = activeKeysFromV2;
          return;
        }

        const activeKeysFromV1 = DEALS_HOME_WIDGET_KEYS.filter((item) => Boolean(parsed?.[item]));
        this.activeWidgetKeys = activeKeysFromV1;
      } catch (_) {
        this.activeWidgetKeys = cloneDefaultActiveWidgetKeys();
      }
    },
    async loadHome() {
      this.boardsLoading = true;
      this.widgetsLoading = true;

      try {
        const timezoneOffsetMinutes = this.resolveTimezoneOffsetMinutes();
        const [boardsResult, summaryResult, trendResult] = await Promise.allSettled([
          axios.get("/ncrm/deals/boards", {
            params: {
              ensureDefault: true,
              includeStages: false,
              includeInactive: false,
            },
          }),
          axios.get("/ncrm/deals/home/summary", {
            params: {
              timezone_offset_minutes: timezoneOffsetMinutes,
            },
          }),
          axios.get("/ncrm/deals/home/trend", {
            params: {
              days: 7,
              timezone_offset_minutes: timezoneOffsetMinutes,
            },
          }),
        ]);

        const failures = [];

        if (boardsResult.status === "fulfilled") {
          this.boards = Array.isArray(boardsResult.value?.data?.items) ? boardsResult.value.data.items : [];
        } else {
          failures.push("boards");
          console.error("[ncrm-deals-home] load boards failed:", boardsResult.reason);
          this.boards = [];
        }

        if (summaryResult.status === "fulfilled") {
          this.homeSummary = {
            totals: summaryResult.value?.data?.totals || {},
            status_distribution: Array.isArray(summaryResult.value?.data?.status_distribution)
              ? summaryResult.value.data.status_distribution
              : [],
            board_metrics: Array.isArray(summaryResult.value?.data?.board_metrics)
              ? summaryResult.value.data.board_metrics
              : [],
          };
        } else {
          failures.push("summary");
          console.error("[ncrm-deals-home] load summary widgets failed:", summaryResult.reason);
          this.homeSummary = {
            totals: {},
            status_distribution: [],
            board_metrics: [],
          };
        }

        if (trendResult.status === "fulfilled") {
          this.homeTrend = {
            totals: trendResult.value?.data?.totals || {},
            series: Array.isArray(trendResult.value?.data?.series) ? trendResult.value.data.series : [],
          };
        } else {
          failures.push("trend");
          console.error("[ncrm-deals-home] load trend widgets failed:", trendResult.reason);
          this.homeTrend = {
            totals: {},
            series: [],
          };
        }

        if (failures.length) {
          const messageKey = failures.length === 3
            ? "ncrm.deals.home.messages.loadError"
            : "ncrm.deals.home.messages.partialLoadError";
          this.showSnackbar(this.$t(messageKey), failures.length === 3 ? "error" : "warning");
        }
      } catch (error) {
        console.error("[ncrm-deals-home] load error", error);
        this.showSnackbar(this.$t("ncrm.deals.home.messages.loadError"), "error");
      } finally {
        this.boardsLoading = false;
        this.widgetsLoading = false;
      }
    },
    openBoard(boardId) {
      if (!boardId) return;
      this.$router.push({
        name: "NcrmDealBoard",
        params: { id: String(boardId) },
      });
    },
    handleBoardRowClick(_event, payload) {
      const row = payload?.item?.raw ?? payload?.item ?? payload;
      const boardId = Number(row?.id || 0);
      if (!boardId) return;
      this.openBoard(boardId);
    },
    closeCreateBoardDialog() {
      this.createBoardDialog = false;
      this.createBoardForm = {
        name: "",
      };
    },
    async submitCreateBoard() {
      const name = this.createBoardForm.name.trim();
      if (!name) {
        this.showSnackbar(this.$t("ncrm.deals.home.createBoard.name"), "warning");
        return;
      }

      this.createBoardLoading = true;
      try {
        await axios.post("/ncrm/deals/boards", { name });
        this.showSnackbar(this.$t("ncrm.deals.home.messages.boardCreated"), "success");
        this.closeCreateBoardDialog();
        await this.loadHome();
      } catch (error) {
        console.error("[ncrm-deals-home] create board error", error);
        this.showSnackbar(
          error?.response?.data?.error || this.$t("ncrm.deals.home.messages.createBoardError"),
          "error"
        );
      } finally {
        this.createBoardLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Table ──────────────────────────────────────────────────────────────── */
.ncrm-deals-home__table-date {
  color: rgba(var(--v-theme-on-surface), 0.72);
  font-size: 0.8rem;
  white-space: nowrap;
}

.ncrm-deals-home__table-overdue {
  color: rgba(var(--v-theme-on-surface), 0.6);
}
.ncrm-deals-home__table-overdue--active {
  color: rgb(var(--v-theme-error));
  font-weight: 600;
}

.ncrm-deals-home__table-chevron {
  opacity: 0;
  color: rgba(var(--v-theme-on-surface), 0.38);
  transition: opacity 0.15s ease;
}
:deep(.ncrm-deals-home__table tbody tr:hover .ncrm-deals-home__table-chevron) {
  opacity: 1;
}
:deep(.ncrm-deals-home__table tbody tr:hover > td) {
  background-color: var(--ncrm-color-hover-strong) !important;
}

/* ── Widgets ────────────────────────────────────────────────────────────── */
.ncrm-deals-home__widget-card {
  border-radius: 12px;
}

.ncrm-deals-home__trend-row:last-child {
  margin-bottom: 0;
}

.ncrm-deals-home__trend-date {
  min-width: 44px;
}

.ncrm-deals-home__trend-total {
  min-width: 36px;
  text-align: right;
}

.ncrm-deals-home__widget-list {
  border: 1px solid rgba(var(--v-theme-outline), 0.3);
  border-radius: var(--app-radius-base);
}
</style>
